import { Client, Owner, OwnerOwnerCategoryEnum } from 'api'
import { getActiveClient } from 'app/selectors'
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { IndividualShareholderNode } from './individualShareholderNode'
import { EntityShareholderNode } from './entityShareholderNode'
import { Button } from 'reactstrap'
import rightArrow from 'assets/images/Right-Arrow-White.svg'
import { getTotalSharePercentageClient, getTotalSharePercentageOwner } from '../uboManagerHelper'

interface Props {
  editShareholder: (shareholder: Owner) => void
  addShareholderToOwner: (shareholder: Owner) => void
  addShareholderToClient: () => void
  onContinue: () => void
}

export const ShareholderConfirmationTree = ({
  editShareholder,
  addShareholderToOwner,
  addShareholderToClient,
  onContinue,
}: Props): ReactElement => {
  const activeClient: Client = useSelector(getActiveClient)

  return (
    <div>
      <h3 className="d-flex align-items-center">
        {activeClient.operatingAs} Shareholders
        {getTotalSharePercentageClient(activeClient, null) < 100.0 && (
          <Button
            type="button"
            color="primary"
            outline
            className="btn-rotessa-primary ml-3"
            size="sm"
            onClick={() => addShareholderToClient()}
          >
            <i className="mdi mdi-plus" />
          </Button>
        )}
      </h3>
      <DisplayShareholders
        shareholders={activeClient.ownersAttributes?.filter((o) => !o.parentOwner) || []}
        editShareholder={editShareholder}
        addShareholderToOwner={addShareholderToOwner}
        addShareholderToClient={addShareholderToClient}
        isRoot={true}
      />
      <div className="flex justify-content-end mt-4">
        <Button
          title="Confirm"
          aria-label="Confirm"
          className="next-button d-flex align-items-center justify-content-center"
          color="primary"
          onClick={onContinue}
          disabled={findOrganizationWithNoShareholders(activeClient.ownersAttributes || [])}
        >
          Next
          <img src={rightArrow} className="ml-1" />
        </Button>
      </div>
    </div>
  )
}

interface DisplayProps {
  editShareholder: (shareholder: Owner) => void
  addShareholderToOwner: (shareholder: Owner) => void
  addShareholderToClient: () => void
  shareholders: Owner[] | undefined
  isRoot: boolean
}

export const DisplayShareholders = ({
  shareholders,
  editShareholder,
  addShareholderToOwner: addShareholderForClient,
  isRoot,
}: DisplayProps): ReactElement => {
  const activeClient: Client = useSelector(getActiveClient)
  return (
    <div>
      {shareholders?.map((shareholder) =>
        shareholder.ownerCategory === OwnerOwnerCategoryEnum.Individual ||
        shareholder.ownerCategory === OwnerOwnerCategoryEnum.ControlPerson ? (
          <IndividualShareholderNode
            key={shareholder.id}
            shareholder={shareholder}
            editShareholder={editShareholder}
            addShareholderToOwner={addShareholderForClient}
            isRoot={isRoot}
          />
        ) : (
          <EntityShareholderNode
            key={shareholder.id}
            shareholder={shareholder}
            editShareholder={editShareholder}
            addShareholderToOwner={addShareholderForClient}
            isRoot={isRoot}
            displayAddButton={getTotalSharePercentageOwner(activeClient, shareholder.id ?? 0, null) < 100.0}
          />
        ),
      )}
    </div>
  )
}

const findOrganizationWithNoShareholders = (owners: Owner[]): boolean => {
  for (const owner of owners) {
    if (owner.ownerCategory === OwnerOwnerCategoryEnum.Entity) {
      // If this entity has no shareholders, return it
      if (!owner.shareholdersAttributes || owner.shareholdersAttributes.length === 0) {
        return true
      }
      // Otherwise, recursively check its shareholders
      const found = findOrganizationWithNoShareholders(owner.shareholdersAttributes)
      if (found) {
        return found
      }
    }
  }
  return false
}
